import {
  type InfiniteData,
  type QueryKey,
  useMutation,
  useQueryClient
} from 'react-query'

import { type PollAnswer } from 'components-ref/molecules'
import { answerPoll } from 'services/endpoints/post'
import { useFollowingFeedCacheStore } from 'store/followingFeedCacheStore'
import {
  type StoriesData,
  type Story,
  type StoryFeedGroup
} from 'types/api/story'
import { type IInfinitePage } from 'types/interfaces/ITypes'

import {
  updateHomeFeedStoryPoll,
  updatePollStories,
  updatePollStory
} from './storyPollCache'

type StoryPollParams = PollAnswer & {
  storyId?: string
}

interface Props {
  story?: Story
}

interface MutationContext {
  previousQueries: Array<[QueryKey, unknown]>
  previousZustandFeeds: Record<string, any[]>
}

export function useStoryPollOptimistic({
  story
}: Props = {}) {
  const queryClient = useQueryClient()

  return useMutation(
    async (params: StoryPollParams) => await answerPoll(params),
    {
      onMutate: async (
        params
      ): Promise<MutationContext> => {
        const storyId = String(params.storyId ?? story?.id ?? '')

        if (!storyId) {
          throw new Error('Missing story ID for poll answer')
        }

        const prefixes = [
          'allStoryPosts',
          'userStories',
          'storyFeed',
          'getHomeFeed'
        ]

        await Promise.all(
          prefixes.map(async prefix =>
            await queryClient.cancelQueries(prefix)
          )
        )

        const previousQueries: Array<[QueryKey, unknown]> = []

        for (const prefix of prefixes) {
          for (const [key, data] of queryClient.getQueriesData(prefix)) {
            if (data !== undefined) {
              previousQueries.push([key, data])
            }
          }
        }

        const previousZustandFeeds = {
          ...useFollowingFeedCacheStore.getState().feedCache
        }

      // Legacy story posts
      const legacyStoryQueries =
        queryClient.getQueriesData<StoriesData>(
          'allStoryPosts'
        )

      for (const [key, data] of legacyStoryQueries) {
        if (!data?.story) continue

        queryClient.setQueryData<StoriesData>(
          key,
          {
            ...data,
            story: data.story.map(post => ({
              ...post,
              stories: updatePollStories(
                post.stories,
                storyId,
                params
              )
            }))
          }
        )
      }
      // Creator profile stories
      const userStoryQueries =
        queryClient.getQueriesData<Story[]>('userStories')

      for (const [key, data] of userStoryQueries) {
        if (!Array.isArray(data)) continue

        queryClient.setQueryData<Story[]>(
          key,
          updatePollStories(data, storyId, params)
        )
      }
        // Grouped stories above the home feed
        const storyFeedQueries =
          queryClient.getQueriesData<StoryFeedGroup[]>(
            'storyFeed'
          )

        for (const [key, data] of storyFeedQueries) {
          if (!Array.isArray(data)) continue

          queryClient.setQueryData<StoryFeedGroup[]>(
            key,
            data.map(group => {
              if (!Array.isArray(group.stories)) {
                return group
              }

              return {
                ...group,
                stories: updatePollStories(
                  group.stories,
                  storyId,
                  params
                )
              }
            })
          )
        }

        // Every existing home-feed query / list / filter
        queryClient.setQueriesData<InfiniteData<IInfinitePage>>(
          'getHomeFeed',
          old => {
            if (!old?.pages) {
              return old ?? { pages: [], pageParams: [] }
            }

            return updateHomeFeedStoryPoll(
              old,
              storyId,
              params
            )
          }
        )

        // Persisted home-feed copies
        const store = useFollowingFeedCacheStore.getState()

        for (const [key, posts] of Object.entries(
          store.feedCache
        )) {
          if (!Array.isArray(posts)) continue

          const updated = posts.map(post => {
            if (!Array.isArray(post.stories)) return post

            const stories = updatePollStories(
              post.stories,
              storyId,
              params
            )

            if (stories === post.stories) return post

            return { ...post, stories }
          })

          if (updated.some((post, index) => post !== posts[index])) {
            store.setFeed(key, updated)
          }
        }

        return {
          previousQueries,
          previousZustandFeeds
        }
      },

      onError: (_error, _params, context) => {
        if (!context) return

        // Restore React Query
        for (const [key, data] of context.previousQueries) {
          queryClient.setQueryData(key, data)
        }

        // Restore persisted home feeds
        const store = useFollowingFeedCacheStore.getState()

        for (const [key, posts] of Object.entries(
          context.previousZustandFeeds
        )) {
          store.setFeed(key, posts)
        }
      }
    }
  )
}